import { useEffect, useState } from 'react';

import type { DocNode } from '@atlaskit/adf-schema/doc';

type TextNode = { text?: string; content?: TextNode[] };

/**
 * Whether the document has more than `threshold` words. A word is a run of non-whitespace in a text
 * node, and the walk stops as soon as the threshold is passed, so long pages don't cost a full count.
 */
const hasMoreWordsThan = (doc: DocNode, threshold: number): boolean => {
	let wordCount = 0;
	const visit = (node: TextNode): boolean => {
		wordCount += node.text?.match(/\S+/g)?.length ?? 0;
		if (wordCount > threshold) {
			return true;
		}
		return node.content?.some(visit) ?? false;
	};
	return visit(doc as TextNode);
};

// How long the user must stop typing before the page is counted again.
const DOCUMENT_CHANGE_DEBOUNCE_MS = 3000;

type UseDetectPageWordCountParams = {
	contentId: string;
	threshold: number;
	/** Reads the live editor document. Should be stable: a new function counts as a new source. */
	getAdfDocument: () => Promise<DocNode | null>;
	/** Subscribes to edits in the editor. Returns an unsubscribe function. */
	subscribeToDocumentChange?: (listener: () => void) => () => void;
};

/**
 * Watches the live editor document and returns whether it is over `threshold` words. It stays `true` once it has been, so
 * a page that is over the threshold doesn't flicker. While it is under, it counts again whenever
 * the document source changes (the editor becoming ready) and a few seconds after the user stops
 * typing, so a page the user is writing starts to qualify once it reaches the threshold.
 */
export const useDetectPageWordCount = ({
	contentId,
	threshold,
	getAdfDocument,
	subscribeToDocumentChange,
}: UseDetectPageWordCountParams): boolean => {
	const [overWordCountContentId, setOverWordCountContentId] = useState<string | null>(null);
	const isOverWordCount = overWordCountContentId === contentId;

	useEffect(() => {
		if (isOverWordCount) {
			return;
		}
		let isCancelled = false;
		let debounceTimeoutId: ReturnType<typeof setTimeout> | undefined;

		const checkDocument = () => {
			getAdfDocument()
				.then((doc) => {
					if (isCancelled || !doc) {
						return;
					}
					if (hasMoreWordsThan(doc, threshold)) {
						setOverWordCountContentId(contentId);
					}
				})
				.catch(() => {
					// Optional UI: if the document can't be read, the nudge stays hidden.
				});
		};

		checkDocument();

		const unsubscribe = subscribeToDocumentChange?.(() => {
			clearTimeout(debounceTimeoutId);
			debounceTimeoutId = setTimeout(checkDocument, DOCUMENT_CHANGE_DEBOUNCE_MS);
		});

		return () => {
			isCancelled = true;
			clearTimeout(debounceTimeoutId);
			unsubscribe?.();
		};
	}, [contentId, threshold, getAdfDocument, subscribeToDocumentChange, isOverWordCount]);

	return isOverWordCount;
};
